import { useState, useEffect } from 'react';
import { requestNotificationPermission, onMessageListener } from '../firebase-config';
import axiosHttp from '../services/interceptor.service';

/**
 * Custom hook for managing Firebase push notifications
 * @param {string} userId - The current user's ID
 * @returns {Object} Notification state and methods
 */
const useNotifications = (userId) => {
    const [notificationPermission, setNotificationPermission] = useState(Notification.permission);
    const [fcmToken, setFcmToken] = useState(null);
    const [foregroundMessage, setForegroundMessage] = useState(null);

    // Function to manually register FCM token
    const registerFCM = async (uid) => {
        const targetUserId = uid || userId;
        console.log('🔔 [useNotifications] registerFCM called for userId:', targetUserId);

        if (!targetUserId) {
            console.log('⚠️  [useNotifications] No userId provided');
            return;
        }

        try {
            console.log('🚀 [useNotifications] Starting notification initialization...');

            if (Notification.permission === 'granted') {
                console.log('✅ [useNotifications] Permission already granted, fetching token...');
            }

            const token = await requestNotificationPermission();

            if (token) {
                console.log('✅ [useNotifications] FCM token received:', token.substring(0, 20) + '...');
                setFcmToken(token);
                setNotificationPermission('granted');

                console.log('💾 [useNotifications] Saving token to backend...');
                await saveFcmTokenToBackend(targetUserId, token);
            } else {
                console.warn('⚠️  [useNotifications] No token received from Firebase');
                setNotificationPermission(Notification.permission);
            }
        } catch (error) {
            console.error('❌ [useNotifications] Error initializing notifications:', error);
        }
    };

    // Auto-register if userId is present on mount/update
    useEffect(() => {
        if (userId) {
            registerFCM(userId);
        }
    }, [userId]);

    // Listen for foreground messages
    useEffect(() => {
        const unsubscribe = onMessageListener((payload) => {
            console.log('🔔 [useNotifications] Foreground message received:', payload);
            setForegroundMessage(payload);

            // Play custom sound
            playNotificationSound();

            // Show browser notification
            if (Notification.permission === 'granted') {
                // Creating manual notification for foreground if desired, or relying on custom UI
                // If you want a browser notification even in foreground:
                const n = new Notification(payload.notification?.title || 'New Notification', {
                    body: payload.notification?.body,
                    icon: payload.notification?.icon || '/logo192.png',
                });
                n.onclick = function (event) {
                    event.preventDefault(); // prevent the browser from focusing the Notification's tab
                    window.location.href = '/home/todoTask'; // Navigate to todoTask
                }
            }
        });

        return () => {
            if (typeof unsubscribe === 'function') {
                unsubscribe();
            }
        };
    }, []);

    // Listen for messages from service worker to play sound for background notifications
    useEffect(() => {
        const handleServiceWorkerMessage = (event) => {
            if (event.data && event.data.type === 'PLAY_NOTIFICATION_SOUND') {
                console.log('🔊 [useNotifications] Playing sound for background notification');
                playNotificationSound();
            }
        };

        navigator.serviceWorker?.addEventListener('message', handleServiceWorkerMessage);

        return () => {
            navigator.serviceWorker?.removeEventListener('message', handleServiceWorkerMessage);
        };
    }, []);

    /**
     * Play notification sound
     */
    const playNotificationSound = () => {
        console.log('🔊 [playNotificationSound] Attempting to play notification sound...');
        try {
            const audio = new Audio('/notification.mp3');
            audio.volume = 1.0; // Set volume to maximum

            // Add event listeners for debugging
            audio.addEventListener('loadeddata', () => {
                console.log('✅ [playNotificationSound] Audio file loaded successfully');
            });

            audio.addEventListener('error', (e) => {
                console.error('❌ [playNotificationSound] Audio loading error:', e);
                console.error('❌ [playNotificationSound] Error details:', audio.error);
            });

            const playPromise = audio.play();

            if (playPromise !== undefined) {
                playPromise
                    .then(() => {
                        console.log('✅ [playNotificationSound] Sound played successfully!');
                    })
                    .catch(e => {
                        console.error('🔇 [playNotificationSound] Audio play failed:', e.name, e.message);
                        if (e.name === 'NotAllowedError') {
                            console.warn('⚠️ [playNotificationSound] User interaction required before playing sound');
                        }
                    });
            }
        } catch (error) {
            console.error('❌ [playNotificationSound] Error creating/playing audio:', error);
        }
    };

    /**
     * Save FCM token to backend
     */
    const saveFcmTokenToBackend = async (userId, token) => {
        try {
            console.log('📡 [saveFcmTokenToBackend] Sending request to backend...');
            console.log('📡 [saveFcmTokenToBackend] URL:', `${process.env.REACT_APP_BASE_URL}/lmt/fcm/save-token`);
            console.log('📡 [saveFcmTokenToBackend] UserId:', userId);
            console.log('📡 [saveFcmTokenToBackend] Token length:', token.length);

            // Use the interceptor which automatically adds the token and user_id headers
            const response = await axiosHttp.post(
                `${process.env.REACT_APP_BASE_URL}/lmt/fcm/save-token`,
                {
                    userId,
                    fcmToken: token,
                }
            );

            console.log('✅ [saveFcmTokenToBackend] FCM token saved to backend successfully!');
            return response.data;
        } catch (error) {
            console.error('❌ [saveFcmTokenToBackend] Error saving FCM token:', error);
            if (error.response) {
                console.error('❌ [saveFcmTokenToBackend] Error details:', error.response.data);
            }
        }
    };

    /**
     * Remove FCM token from backend (on logout)
     */
    const removeFcmToken = async (uId) => {
        try {
            await axiosHttp.post(`${process.env.REACT_APP_BASE_URL}/lmt/fcm/remove-token`, {
                userId: uId || userId
            });
            console.log('✅ [useNotifications] FCM token removed from backend');
        } catch (error) {
            console.error('❌ [useNotifications] Error removing FCM token:', error);
        }
    };

    /**
     * Clear foreground message
     */
    const clearForegroundMessage = () => {
        setForegroundMessage(null);
    };

    return {
        notificationPermission,
        fcmToken,
        foregroundMessage,
        clearForegroundMessage,
        clearForegroundMessage,
        removeFcmToken,
        registerFCM, // Export this function
    };
};

export default useNotifications;
